import { faCaretDown, faCaretUp, faExclamationTriangle, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { ErrorMessage, useFormikContext } from 'formik'
import numeral from 'numeral'
import React, { useEffect, useMemo } from 'react'
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { checkExemption } from '../api/registration'
import { AppDispatch } from '../store'
import {
  fetchTownDependencies,
  fetchTowns,
  fetchWorkplacesFiltered,
  periodsSelector,
  resetWorkplacesFiltered,
  townsSelector,
} from '../store/createApplicationSlice'
import { RootState } from '../store/rootReducer'
import { isAdultAge } from '../utils/formUtils'
import { CreateApplicationFormValues } from './CreateApplication'
import { hasDisclaimerContent } from './CreateApplicationDisclaimer'

const CreateApplicationData = (): React.ReactElement | null => {
  const { id } = useParams<{ id: string | undefined }>()
  const dispatch = useDispatch<AppDispatch>()
  const { currentUser } = useSelector((state: RootState) => state.auth)
  const {
    townsLoading,
    townsError,
    workplaceTypes,
    workplaceTypesError,
    workplaceTypesLoading,
    workplaceDistricts,
    workplaceDistrictsError,
    workplaceDistrictsLoading,
    workplaces,
    workplacesLoading,
    workplacesError,
    workplacesFiltered,
    workplacesFilteredLoading,
    periodsError,
    periodsLoading,
  } = useSelector((state: RootState) => state.createApplication)
  const { t } = useTranslation(['createApplication', 'common'])
  const { values, errors, getFieldProps, touched, handleChange, setFieldValue, setFieldTouched } =
    useFormikContext<CreateApplicationFormValues>()

  const towns = useSelector(townsSelector.selectAll)
  const periods = useSelector(periodsSelector.selectAll)
  const selectedTown = useSelector((state: RootState) => townsSelector.selectById(state, values.town || -1))
  const selectedPeriod = useSelector((state: RootState) => periodsSelector.selectById(state, values.period || -1))
  const periodParts = useMemo(() => {
    if (selectedPeriod && selectedPeriod.allParts && values.birthYear != null) {
      if (selectedPeriod.ageExclusionParts) {
        return selectedPeriod.allParts.filter(
          (x) => !x.ageExclusion || x.ageExclusion.indexOf(`${values.birthYear}`) > -1
        )
      }
      return selectedPeriod.allParts
    }
    return []
  }, [selectedPeriod, values.birthYear])
  const workplacesSelected = useMemo(() => {
    return workplaces
      ? workplaces
          .filter((w) => values.workplace.includes(w.id))
          .sort((a, b) => values.workplace.indexOf(a.id) - values.workplace.indexOf(b.id))
      : []
  }, [workplaces, values.workplace])

  useEffect(() => {
    dispatch(fetchTowns())
  }, [dispatch])

  const onTownChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFieldValue('workplaceType', '')
    setFieldValue('workplaceDistrict', '')
    setFieldValue('workplace', [])
    setFieldValue('period', '')
    setFieldValue('parts', [])
    setFieldValue('hasParts', false)
    setFieldValue('numPartsApplication', 0)
    setFieldValue('numWorkplacesApplication', 0)
    setFieldValue('prioritizeWorkplacesApplication', false)
    setFieldValue('filterWorkplacesApplication', false)

    if (e.target.value) {
      checkExemption(e.target.value, values.ssn)
        .then((r) => {
          setFieldValue('birthYear', r.data.year)
          setFieldValue('ageYear', r.data.age_year)
          setFieldValue('isExemption', r.data.is_exemption)
          dispatch(fetchTownDependencies(e.target.value, values.ssn))
        })
        .catch(() => {
          toast.error(t('common:kerfisvilla'))
        })
    }
  }

  const onPeriodChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFieldValue('parts', [])
  }

  useEffect(() => {
    if (selectedPeriod && !selectedPeriod.ageExclusionParts) {
      setFieldValue('numPartsApplication', selectedPeriod.numPartsApplication)
    } else {
      setFieldValue('numPartsApplication', 0)
    }

    if (selectedPeriod) {
      setFieldValue('hasParts', selectedPeriod.hasParts)
    } else {
      setFieldValue('hasParts', false)
    }
  }, [setFieldValue, selectedPeriod])

  useEffect(() => {
    if (selectedTown) {
      setFieldValue('numWorkplacesApplication', selectedTown.numWorkplacesApplication)
      setFieldValue('prioritizeWorkplacesApplication', selectedTown.prioritizeWorkplacesApplication)
      setFieldValue('filterWorkplacesApplication', selectedTown.filterWorkplacesApplication)
      const hasDisclaimer =
        hasDisclaimerContent(selectedTown.disclaimerText) || hasDisclaimerContent(selectedTown.disclaimerTextEn)
      setFieldValue('hasDisclaimer', hasDisclaimer)
      setFieldValue('disclaimerAccepted', false)
    } else {
      setFieldValue('numWorkplacesApplication', 0)
      setFieldValue('prioritizeWorkplacesApplication', false)
      setFieldValue('filterWorkplacesApplication', false)
      setFieldValue('hasDisclaimer', false)
      setFieldValue('disclaimerAccepted', false)
    }
  }, [setFieldValue, selectedTown])

  useEffect(() => {
    if (values.town && values.ssn) {
      if (!values.workplaceType && !values.workplaceDistrict) {
        dispatch(resetWorkplacesFiltered())
      } else {
        dispatch(
          fetchWorkplacesFiltered({
            town: values.town,
            type: values.workplaceType,
            district: values.workplaceDistrict,
          })
        )
      }
    }
  }, [dispatch, values.town, values.ssn, values.workplaceType, values.workplaceDistrict])

  const swapWorkplace = (idx1: number, idx2: number) => {
    const list = [...values.workplace]
    const w = list[idx1]
    list[idx1] = list[idx2]
    list[idx2] = w
    setFieldValue('workplace', list)
  }

  const moveWorkplaceUp = (idx: number) => {
    if (idx > 0) {
      swapWorkplace(idx, idx - 1)
    }
  }

  const moveWorkplaceDown = (idx: number) => {
    if (idx < workplacesSelected.length - 1) {
      swapWorkplace(idx, idx + 1)
    }
  }

  if (!values.ssn || !values.ssnValid) {
    return null
  }

  return (
    <Card className="mb-2">
      <Card.Body>
        <Row>
          <Col>
            <h4>{t('skraning')}</h4>
          </Col>
        </Row>
        <Row>
          <Col md="4">
            <p className="small text-muted">{t('skraningHjalp')}</p>
          </Col>
          <Col md="8">
            <Form.Group controlId="town">
              <Form.Label>{t('sveitarfelag')}*:</Form.Label>
              <Form.Control
                as="select"
                className={classNames({
                  'is-invalid': touched.town && errors.town,
                })}
                disabled={id != null}
                {...getFieldProps('town')}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  handleChange(e)
                  onTownChanged(e)
                }}
              >
                <option value="">{t('velduSveitarfelag')}</option>
                {towns &&
                  towns.map((town) => (
                    <option key={town.id} value={town.id}>
                      {town.name}
                    </option>
                  ))}
              </Form.Control>
              {townsError && <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>}
              {townsLoading === 'idle' && towns?.length === 0 && (
                <div className="invalid-feedback d-block text-left">{t('enginSveitafelog')}</div>
              )}
              <Form.Text className="text-muted">
                {townsLoading === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}
                {t('sveitarfelagSottUm')}
              </Form.Text>
              <ErrorMessage name="town" component="div" className="invalid-feedback d-block text-left" />
            </Form.Group>

            {values.town &&
              selectedTown?.requireGuardianForUnder18 &&
              values.ageNow !== undefined &&
              !isAdultAge(values.ageNow) &&
              currentUser?.ssn === values.ssn && (
                <Alert variant="warning">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                  {t('requireGuardianForUnder18')}
                </Alert>
              )}

            {values.town &&
              !(
                selectedTown?.requireGuardianForUnder18 &&
                values.ageNow !== undefined &&
                !isAdultAge(values.ageNow) &&
                currentUser?.ssn === values.ssn
              ) && (
                <>
                  <Form.Group controlId="workplaceType">
                    <Form.Label>{t('tegundStarfsstadar')}:</Form.Label>
                    <Form.Control as="select" {...getFieldProps('workplaceType')}>
                      <option value="">{t('velduTegundStarfsstadar')}</option>
                      {workplaceTypes &&
                        workplaceTypes
                          .filter(
                            (t) =>
                              !values.filterWorkplacesApplication ||
                              t.ageExclusions.length === 0 ||
                              t.ageExclusions.includes(values.ageYear || -1)
                          )
                          .map((workplaceType) => (
                            <option key={workplaceType.id} value={workplaceType.id}>
                              {workplaceType.name}
                            </option>
                          ))}
                    </Form.Control>
                    {workplaceTypesError && (
                      <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>
                    )}
                    <Form.Text className="text-muted">
                      {workplaceTypesLoading === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}
                      {t('tegundStarfsstadarSottUm')}
                    </Form.Text>
                  </Form.Group>

                  {(!workplaceDistricts || workplaceDistricts.length > 0) && (
                    <Form.Group controlId="workplaceDistrict">
                      <Form.Label>{t('hverfiStarfsstadar')}:</Form.Label>
                      <Form.Control as="select" {...getFieldProps('workplaceDistrict')}>
                        <option value="">{t('velduHverfiStarfsstadar')}</option>
                        {workplaceDistricts &&
                          workplaceDistricts.map((workplaceDistrict) => (
                            <option key={workplaceDistrict.id} value={workplaceDistrict.id}>
                              {workplaceDistrict.name}
                            </option>
                          ))}
                      </Form.Control>
                      {workplaceDistrictsError && (
                        <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>
                      )}
                      <Form.Text className="text-muted">
                        {workplaceDistrictsLoading === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}
                        {t('hverfiStarfsstadarSottUm')}
                      </Form.Text>
                    </Form.Group>
                  )}

                  <Form.Group controlId="workplace" className="form-disabled-normal-text">
                    <Form.Label>{t('starfsstadur')}:</Form.Label>
                    {(!values.filterWorkplacesApplication || values.workplaceType) && (
                      <>
                        {values.numWorkplacesApplication > 0 && (
                          <div style={{ fontSize: '90%' }} className="mb-2">
                            <i>
                              {t('veljaHamark')} {values.numWorkplacesApplication}
                            </i>
                          </div>
                        )}
                        {values.prioritizeWorkplacesApplication && <Alert variant="info">{t('veljaForgang')}</Alert>}
                      </>
                    )}

                    {workplacesSelected.map((workplace, idx) => (
                      <div key={`${workplace.id}-checked`}>
                        <Form.Check
                          id={`workplace-${workplace.id}`}
                          type="checkbox"
                          custom
                          label={
                            <>
                              {workplace.name}{' '}
                              <Button
                                role="button"
                                variant="link"
                                className="p-0"
                                size="sm"
                                onClick={() => {
                                  setFieldValue(
                                    'workplace',
                                    values.workplace.filter((w) => w !== workplace.id)
                                  )
                                  setFieldTouched('workplace')
                                }}
                              >
                                [{t('remove')}]
                              </Button>
                              {values.prioritizeWorkplacesApplication && (
                                <>
                                  {' '}
                                  {idx < workplacesSelected.length - 1 && (
                                    <FontAwesomeIcon
                                      className="text-muted cursor-pointer"
                                      icon={faCaretDown}
                                      fixedWidth
                                      onClick={() => {
                                        moveWorkplaceDown(idx)
                                      }}
                                    />
                                  )}
                                  {idx > 0 && (
                                    <FontAwesomeIcon
                                      className="text-muted cursor-pointer"
                                      icon={faCaretUp}
                                      fixedWidth
                                      onClick={() => {
                                        moveWorkplaceUp(idx)
                                      }}
                                    />
                                  )}
                                </>
                              )}
                            </>
                          }
                          checked
                          disabled
                        />
                      </div>
                    ))}
                    {values.filterWorkplacesApplication && !values.workplaceType && (
                      <div style={{ fontSize: '90%' }}>
                        <i>{t('velduFyrstTegundStarfsstadar')}</i>
                      </div>
                    )}
                    {(!values.filterWorkplacesApplication || values.workplaceType) &&
                      workplacesFiltered &&
                      workplacesFiltered
                        .filter((w) => !values.workplace.includes(w.id))
                        .map((workplace) => (
                          <div key={`${workplace.id}-unchecked`}>
                            <Form.Check
                              id={`workplace-${workplace.id}`}
                              type="checkbox"
                              label={workplace.name}
                              custom
                              checked={false}
                              disabled={
                                values.numWorkplacesApplication > 0 &&
                                values.workplace.length >= values.numWorkplacesApplication
                              }
                              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                                if (e.target.checked) {
                                  setFieldValue('workplace', [...values.workplace, workplace.id])
                                  setFieldTouched('workplace')
                                }
                              }}
                            />
                          </div>
                        ))}
                    {workplacesError && (
                      <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>
                    )}
                    {workplacesLoading === 'idle' && workplaces?.length === 0 && (
                      <div className="invalid-feedback d-block text-left">{t('engirStarfsstadir')}</div>
                    )}
                    {workplacesFilteredLoading === 'idle' && workplacesFiltered?.length === 0 && (
                      <div className="invalid-feedback d-block text-left">{t('engirStarfsstadir')}</div>
                    )}
                    <Form.Text className="text-muted">
                      {(workplacesLoading === 'pending' || workplacesFilteredLoading === 'pending') && (
                        <FontAwesomeIcon spin icon={faSpinner} />
                      )}
                      {t('starfsstadurSottUm')}
                    </Form.Text>
                    <ErrorMessage name="workplace" component="div" className="invalid-feedback d-block text-left" />
                  </Form.Group>

                  <Form.Group controlId="period">
                    <Form.Label>{t('timabil')}*:</Form.Label>
                    <Form.Control
                      as="select"
                      className={classNames({
                        'is-invalid': touched.period && errors.period,
                      })}
                      disabled={id != null}
                      {...getFieldProps('period')}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        handleChange(e)
                        onPeriodChanged(e)
                      }}
                    >
                      <option value="">{t('velduTimabil')}</option>
                      {periods &&
                        periods.map((period) => (
                          <option key={period.id} value={period.id}>
                            {period.name}
                          </option>
                        ))}
                    </Form.Control>
                    {periodsError && (
                      <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>
                    )}
                    {periodsLoading === 'idle' && periods?.length === 0 && (
                      <div className="invalid-feedback d-block text-left">{t('enginTimabil')}</div>
                    )}
                    <Form.Text className="text-muted">
                      {periodsLoading === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}
                      {t('timabilSottUm')}
                    </Form.Text>
                    <ErrorMessage name="period" component="div" className="invalid-feedback d-block text-left" />
                    {selectedPeriod && selectedPeriod.periodMaxHours && (
                      <Alert variant="info" className="mt-3">
                        {t('periodMaxHours', {
                          year: values.birthYear,
                          hours: numeral(selectedPeriod.periodMaxHours).format('0,0.[00]'),
                        })}
                      </Alert>
                    )}
                  </Form.Group>

                  {selectedPeriod && selectedPeriod.hasParts && (
                    <>
                      <Form.Group controlId="parts">
                        <Form.Label>{t('starfstimabil')}*:</Form.Label>
                        {!selectedPeriod.ageExclusionParts && (
                          <div style={{ fontSize: '90%' }}>
                            <i>
                              {t('veljaHamark')} {selectedPeriod.numPartsApplication}
                            </i>
                          </div>
                        )}
                        {periodParts &&
                          periodParts.map((part) => (
                            <div key={`${part.id}-unchecked`}>
                              <Form.Check
                                id={`part-${part.id}`}
                                type="checkbox"
                                label={
                                  <>
                                    {dayjs(part.dateFrom).format('DD.MM.YYYY')} -{' '}
                                    {dayjs(part.dateTo).format('DD.MM.YYYY')}
                                  </>
                                }
                                custom
                                checked={values.parts.includes(part.id)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                                  if (e.target.checked) {
                                    setFieldValue('parts', [...values.parts, part.id])
                                  } else {
                                    setFieldValue(
                                      'parts',
                                      values.parts.filter((p) => p !== part.id)
                                    )
                                  }
                                  setFieldTouched('parts')
                                }}
                              />
                            </div>
                          ))}
                        <Form.Text className="text-muted">{t('starfstimabilSottUm')}</Form.Text>
                        <ErrorMessage name="parts" component="div" className="invalid-feedback d-block text-left" />
                      </Form.Group>
                    </>
                  )}
                  {/* <Form.Group controlId="otherWorkplace">
                  <Form.Label>{t('annadStarfsstadur')}:</Form.Label>
                  <Form.Control as="textarea" {...getFieldProps('otherWorkplace')} />
                  <Form.Text className="text-muted">{t('annadStarfsstadurEfAVid')}</Form.Text>
                </Form.Group> */}
                </>
              )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

export default CreateApplicationData
